import classNames from 'classnames'
import {t} from 'i18next'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {Toast} from 'primereact/toast'
import React, {useEffect} from 'react'
import {Controller, FormProvider, useFieldArray, useForm} from 'react-hook-form'
import {
  cutEnding,
  getDocumentType,
  MAX_DOSSIER_COMMENT_LENGTH,
  MAX_DOSSIER_NAME_LENGTH,
  supportsFileType
} from './Helpers'
import {API_DOSSIERS, MAX_DOSSIER_SIZE, MAX_MULTIUPLOAD} from '../../../constants'
import {Dossier, DossierDb, DossierEntry, signedFileUrls, UploadMask} from '../../../model/dossier.model'
import {DropdownItem} from '../../../model/dropdownItem.model'
import {RequestMethod} from '../../../model/enums/enums'
import {Person} from '../../../model/person.model'
import {useAppSelector} from '../../../redux/hooks'
import {handleRequest} from '../../../services/api/base/handleRequest'

/** This hook contains a simple form to upload documents into the dossier of a prechosen person */
export const DocumentUpload = (props: {
  files: File[]; // files that are currently chosen for upload
  person: Person | undefined; // person to whom to upload
  defaultValues: UploadMask[] | undefined; // default value for the form
  toastRef: React.RefObject<Toast>; // toast ref to display success/error messages
  currentDossier: DossierDb[]; // the current dossier of this person, used for duplicate checks
  showDeleteButton: boolean; // in certain cases, no delete button is displayed
  removeFile: (arg0: number) => void; // callback for removing a file from the uploaded list
  // arg0: index of the file in the form array
  cleanupCallback: (arg0: boolean) => void; // callback to cleanup
  // arg0: saying if the file has changes
}): JSX.Element => {
  const user = useAppSelector((state) => state.user)
  const documentTypes = useAppSelector((state) => state.documentTypes)

  const documentTypesDropdown: DropdownItem[] = documentTypes.map((type) => {
    return {
      label: t<string>(`dossier.document_types.${type.document_type_id}`),
      value: type.document_type_id,
    }
  })

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      fa: props.defaultValues,
    },
  })

  const { fields, remove, append } = useFieldArray({
    control: methods.control,
    name: 'fa',
    shouldUnregister: true,
  })

  // we are using all controlled inputs
  const watchFieldArray = methods.watch('fa')
  const controlledFields = fields.map((field, index) => {
    if (watchFieldArray) {
      return {
        ...field,
        ...watchFieldArray[index],
      }
    }
  })

  // this effect handles the case when more files are added
  useEffect(() => {
    // we have added files

    // this is a dirty hack so that we do not apply doubly in the first render
    if (
      props.files.length > controlledFields.length &&
      controlledFields.length !== 0
    ) {
      const toAppend: UploadMask[] = props.files
        .slice(controlledFields.length)
        .map((v) => {
          return {
            document_name: cutEnding(v.name),
            comment: '',
            type: getDocumentType(v.name, documentTypes),
          }
        })

      append(toAppend)
    }
  }, [props.files])

  const onSubmit = async (): Promise<void> => {
    // should never happen
    if (!props.files) return

    // we only submit if the file type is supported
    // we don't handle this in the form since the user cannot change this
    for (let file of props.files) {
      const spl = file.name.split('.')
      const type = spl[spl.length - 1]
      if (!supportsFileType(type)) {
        props.toastRef.current?.show({
          closable: true,
          severity: 'error',
          summary: t<string>('dossier.type_no_support.title'),
          detail: t<string>('dossier.type_no_support.message', { type: type }),
          life: 4000,
        })
        return
      }
    }
    if (props.files.reduce((prev, cur) => prev + cur.size, 0) > MAX_DOSSIER_SIZE) {
      props.toastRef.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('dossier.file_size.title'),
        detail: t<string>('dossier.file_size.message'),
        life: 4000,
      })
      return
    }

    if (!props.person?.person_id) {
      console.error('No person defined')
      return
    }

    if (controlledFields.length !== props.files.length) {
      console.error('Fields and File missmatch')
      return
    }

    if (!props.files) {
      console.error('Files not registered properly')
      return
    }

    // submit the form entries
    const entries: DossierEntry[] = controlledFields.map((val, index) => {
      let fileEnding
      if (props.files) {
        fileEnding = props.files[index].name.split('.')
        fileEnding = fileEnding[fileEnding.length - 1]
      }

      const entry: DossierEntry = {
        comment: val?.comment,
        // make the transpiler happy
        size: props.files ? props.files[index].size : 0,
        name: fileEnding ? `${val?.document_name}.${fileEnding}` : '',
        documenttype: val?.type ? val.type : 1,
      }
      return entry
    })

    const body: Dossier = {
      id_person: props.person?.person_id,
      id_creator: user.personId,
      dossier_entries: entries,
    }

    const result = await handleRequest<signedFileUrls>(
      API_DOSSIERS(),
      RequestMethod.POST,
      body
    )

    if (result.error) {
      return
    }

    const signedUrls = result?.data?.url_arr
    if (!signedUrls) {
      console.error('signed urls are not provided')
      return
    }

    if (signedUrls.length !== props.files.length) {
      console.error('URL and File missmatch')
      return
    }
    let i = 0
    for (let url of signedUrls) {
      const uploadResult = await handleRequest<any>(
        url,
        RequestMethod.UPLOAD,
        props.files[i],
        undefined,
        true
      )
      // this should not happen
      if (uploadResult.error) {
        console.error(uploadResult)
        //TODO handle error
      }
    }

    methods.reset()
    props.toastRef.current?.clear()
    props.toastRef.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('dossier.upload.title'),
      detail: t<string>('dossier.upload.message'),
      life: 4000,
    })

    props.cleanupCallback(true)
  }

  const removeFileHandler = (idx: number): void => {
    // if we have only one file left and we delete it
    // we simply close the form and cleanup without changes
    if (props.files?.length === 1) {
      methods.reset()
      props.cleanupCallback(false)
      return
    }

    // else we keep the form and remove the file
    props.removeFile(idx)
    remove(idx)
  }

  const validateDuplicates = (value: string, index: number): boolean => {
    const curValues = methods.getValues('fa')
    if (!props.files || !curValues) return false
    const spl = props.files[index].name.split('.')
    const ending = spl[spl.length - 1]
    // we check if there is file of same datatype and name in db
    // or in the current form
    if (
      !props.currentDossier?.find((e) => e.filename === `${value}.${ending}`) &&
      !(
        curValues.filter(
          (e) => `${e?.document_name}.${ending}` === `${value}.${ending}`
        ).length > 1
      )
    ) {
      return true
    }
    return false
  }

  const hasNameError = (index: number) => {
    const fa = methods.formState.errors.fa
    return fa && fa[index]?.document_name?.type === 'pattern'
  }

  const validationError = (index: number): JSX.Element => {
    // name pattern,
    if (hasNameError(index)) {
      return (
        <small
          className={classNames({
            'p-error': methods.formState.errors.fa?.at?.(index)?.document_name,
          })}
        >
          {t<string>('dossier.invalid_name')}
        </small>
      )
    }

    // duplicate
    if (
      methods.formState.errors.fa?.at?.(index)?.document_name?.type ===
      'validate'
    ) {
      return (
        <small
          className={classNames({
            'p-error': methods.formState.errors.fa?.at(index)?.document_name,
          })}
        >
          {t<string>('dossier.duplicate')}
        </small>
      )
    }

    // length of name
    if (
      methods.formState.errors.fa?.at?.(index)?.document_name?.type ===
      'maxLength'
    ) {
      const curLength = methods.getValues(`fa.${index}.document_name`).length

      return (
        <small
          className={classNames({
            'p-error': methods.formState.errors.fa?.at(index)?.document_name,
          })}
        >
          {t<string>('dossier.max_length', {
            length: curLength,
            of: MAX_DOSSIER_NAME_LENGTH,
          })}
        </small>
      )
    }

    return <></>
  }

  const validationErrorComment = (index: number) => {
    // length of name
    if (
      methods.formState.errors.fa?.at?.(index)?.comment?.type === 'maxLength'
    ) {
      const curLength = methods.getValues(`fa.${index}.comment`).length
      return (
        <small
          className={classNames({
            'p-error': methods.formState.errors.fa?.at(index)?.document_name,
          })}
        >
          {t<string>('dossier.max_length', {
            length: curLength,
            of: MAX_DOSSIER_COMMENT_LENGTH,
          })}
        </small>
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className={'formgrid p-fluid grid pl-2'}>
          {controlledFields.map((item, index) => {
            return (
              <div className={'col-12 grid mt-1 pr-0'} key={item?.id}>
                <div className="col-10 mt-2">
                  <div className="p-fluid formgrid grid">
                    <div className="col-12 md:col-4 field">
                      <label
                        htmlFor={`fa.${index}.document_name`}
                        className={classNames({
                          'p-error':
                            methods.formState.errors.fa?.at?.(index)
                              ?.document_name,
                        })}
                      >
                        {t<string>('core.file_name')}
                      </label>
                      {/* the regex can be overthought and changed */}
                      <Controller
                        name={`fa.${index}.document_name`}
                        control={methods.control}
                        rules={{
                          required: true,
                          validate: (value) => validateDuplicates(value, index),
                          pattern:
                            /^[\u00C0-\u017Fa-zA-Z0-9](?:[\u00C0-\u017Fa-zA-Z0-9()._-]*[\u00C0-\u017Fa-zA-Z0-9)])?$/,
                          maxLength: MAX_DOSSIER_NAME_LENGTH,
                        }}
                        render={({ field, fieldState }) => (
                          <InputText
                            id={field.name}
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              methods.setValue(field.name, e.target.value)
                            }}
                            onBlur={field.onBlur}
                            className={classNames({
                              'p-invalid': fieldState.error,
                            })}
                          />
                        )}
                      />
                      {validationError(index)}
                    </div>
                    <div className="col-12 md:col-4 field">
                      <label
                        htmlFor={`fa.${index}.type`}
                        className={classNames({
                          'p-error':
                            methods.formState.errors.fa?.at?.(index)?.type,
                        })}
                      >
                        {t<string>('dossier.document_type')}
                      </label>
                      <Controller
                        name={`fa.${index}.type`}
                        control={methods.control}
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                          <Dropdown
                            placeholder={t<string>('core.choose')}
                            value={field.value}
                            appendTo={document.body}
                            options={documentTypesDropdown}
                            filter
                            showFilterClear
                            filterBy="label"
                            onChange={(e) => {
                              field.onChange(e.value)
                              methods.setValue(field.name, e.target.value)
                            }}
                            className={classNames({
                              'p-invalid': fieldState.error,
                            })}
                          />
                        )}
                      />
                    </div>
                    <div className="col-12 md:col-4 field">
                      <label
                        htmlFor={`fa.${index}.comment`}
                        className={classNames({
                          'p-error':
                            methods.formState.errors.fa?.at?.(index)?.comment,
                        })}
                      >
                        {t<string>('dossier.comment')}
                      </label>
                      <Controller
                        name={`fa.${index}.comment`}
                        control={methods.control}
                        rules={{ maxLength: MAX_DOSSIER_COMMENT_LENGTH }}
                        render={({ field, fieldState }) => (
                          <InputText
                            id={field.name}
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              methods.setValue(field.name, e.target.value)
                            }}
                            onBlur={field.onBlur}
                            className={classNames({
                              'p-invalid': fieldState.error,
                            })}
                          />
                        )}
                      />
                      {validationErrorComment(index)}
                    </div>
                  </div>
                </div>
                <div className="col-1 mt-2">
                  <div className="grid">
                    <div className="col-12 md:col-8 pt-5">
                      {`${(
                        (props.files?.at(index)?.size as number) / 1e3
                      ).toFixed(2)}kB`}
                    </div>
                    {props.showDeleteButton && (
                      <div
                        className="col-12 md:col-1"
                        key={`fa.${index}.delete`}
                      >
                        <Button
                          icon="fa-solid fa-trash"
                          className="p-button-danger p-button-text mt-4 ml-auto"
                          onClick={(_) => removeFileHandler(index)}
                        ></Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          <div className={'ml-auto mt-2 mr-4 p-fluid grid gap-3'}>
            <Button
              type={'submit'}
              icon="fas fa-file-upload"
              label={t<string>('dossier.store')}
              className="p-button col button-height"
            ></Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}